import { Button, Form, Input, message, Modal } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	getApprovalHierarchyCopy,
	getApprovalHierarchyCopyName,
} from '../../utils/ApprovalHierarchyUtils';
import { updateBuyerCompanyApprovalTiers } from '../../thunks/company_approval_thunks';
import OWButton from '../ow_button/OWButton';

const FORM_ID = 'approval-hierarchy-copy-form';

/**
 * Copies an approval hierarchy under a new name. Naming is the copy: the dialog opens on
 * "Copy of <name>" and the POST only fires once the user has confirmed what the copy is called, so
 * there is never an unnamed duplicate sitting in the list.
 *
 * The copy carries the source's tiers, amounts, escalations and approvers; `onSuccess` receives the
 * created hierarchy so the caller can route to it for any further edits.
 */
function ApprovalHierarchyCopyModal({
	visible,
	approvalHierarchy,
	onCancel,
	onSuccess,
	currentUser,
	createApprovalHierarchy,
}) {
	const [submitting, setSubmitting] = useState(false);

	const handleFinish = (values) => {
		setSubmitting(true);
		createApprovalHierarchy({
			buyerCompanyId: nullSafeGet('facility.buyerCompanyId', currentUser),
			...getApprovalHierarchyCopy(approvalHierarchy, nullSafeGetOrElse('name', values, '').trim()),
		})
			.then((record) => {
				message.success(`Created ${nullSafeGet('name', record)}.`);
				onSuccess(record);
			})
			.catch((err) => message.error(err || 'Unable to copy this approval hierarchy.'))
			.finally(() => setSubmitting(false));
	};

	return (
		<Modal
			visible={visible}
			width={480}
			title="Make a Copy"
			onCancel={onCancel}
			destroyOnClose={true}
			closable={true}
			footer={[
				<Button key="cancel" size="large" onClick={onCancel} disabled={submitting}>
					Cancel
				</Button>,
				<OWButton
					key="submit"
					type="primary"
					size="large"
					htmlType="submit"
					form={FORM_ID}
					loading={submitting}
				>
					Create Copy
				</OWButton>,
			]}
		>
			<Form
				id={FORM_ID}
				layout="vertical"
				preserve={false}
				initialValues={{ name: getApprovalHierarchyCopyName(approvalHierarchy) }}
				onFinish={handleFinish}
			>
				<Form.Item
					// Tighter than antd's default 24px bottom margin so the note below reads as part of
					// the field rather than a detached block. Inline rather than a Tailwind `mb-*`:
					// babel-plugin-import injects antd's CSS after tailwind.out.css, so `.ant-form-item`
					// wins the specificity tie and the utility class is a no-op here.
					style={{ marginBottom: 8 }}
					label="Name"
					name="name"
					rules={[{ required: true, whitespace: true, message: 'Please enter a name.' }]}
				>
					<Input autoFocus={true} placeholder="Approval hierarchy name" />
				</Form.Item>
				<div className="footnoteregular text-character-secondary">
					The tiers, amounts and approvers of{' '}
					{nullSafeGetOrElse('name', approvalHierarchy, 'this hierarchy')} are copied over. Approval
					rules are not.
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	createApprovalHierarchy: (approvalHierarchy) =>
		dispatch(updateBuyerCompanyApprovalTiers(ownProps.userType, approvalHierarchy)()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalHierarchyCopyModal);
